import React from "react";
import SeoHead from "../seo/SeoHead";
import ServicePageShell from "../ui/ServicePageShell";
import { ServiceTag } from "../ui/ServiceTag";
import CallButton from "../ui/CallButton";
import DirectionsButton from "../ui/DirectionsButton";
import BusinessHours from "../ui/BusinessHours";
import ReviewSection from "../ui/ReviewSection";
import TrustBadges from "../ui/TrustBadges";
import VehicleBrandGrid from "../ui/VehicleBrandGrid";
import WarrantyNotice from "../ui/WarrantyNotice";
import ServiceAreas from "../ui/ServiceAreas";
import PageBreadcrumb from "../seo/PageBreadcrumb";
import { Link } from "react-router-dom";
import { PHONE_DISPLAY } from "../../lib/businessInfo";
import heroImg from "../../assets/LandRover.png";

const bulletClass =
  "flex items-start gap-2 text-sm text-slate-700 sm:text-base leading-relaxed";

const LandRoverRepair = () => {
  return (
    <>
      <SeoHead
        title="Land Rover & Range Rover Repair in Temecula | Advanced Automotive"
        description="Land Rover and Range Rover repair, maintenance, and smog checks in Temecula. Diagnostics, 4x4 and air-suspension care, and experienced import service."
        path="/land-rover-repair"
      />

      <ServicePageShell>
        <PageBreadcrumb
          label="Land Rover Repair"
          path="/land-rover-repair"
          description="Land Rover and Range Rover repair, maintenance, and smog checks in Temecula. Diagnostics, 4x4 and air-suspension care, and experienced import service."
          serviceType="Land Rover and Range Rover repair"
        />
        <div className="flex min-w-0 flex-col gap-8 md:flex-row md:items-start md:gap-14">
          <div className="order-1 flex w-full min-w-0 justify-center md:order-none md:w-2/5">
            <div className="relative w-full max-w-xs sm:max-w-sm">
              <div className="absolute -inset-3 rounded-3xl bg-teal-600/10 blur-xl" />
              <img
                src={heroImg}
                alt="Land Rover and 4x4 drivetrain service"
                className="relative aspect-[4/3] h-auto w-full max-h-[14rem] rounded-3xl border border-slate-200 object-cover object-center shadow-xl sm:aspect-auto sm:max-h-none"
              />
            </div>
          </div>

          <div className="w-full min-w-0 md:w-3/5">
            <ServiceTag>Land Rover · Range Rover · 4x4</ServiceTag>
            <h1 className="mb-3 text-[1.35rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-3xl md:text-4xl">
              Land Rover Repair
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-slate-700 sm:text-base">
              Land Rover and Range Rover vehicles combine luxury with capable
              four-wheel-drive systems. Whether you need maintenance, air
              suspension concerns, drivetrain service, or a California smog
              inspection, we are equipped to help keep your SUV road-ready.
            </p>
            <CallButton className="px-8 py-3 text-base" />
          </div>
        </div>

        <TrustBadges tone="light" className="mt-12" />
        <VehicleBrandGrid tone="light" className="mt-10" linked />
        <WarrantyNotice tone="light" className="mt-8" />

        <section className="mt-14 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Signs You May Need This Service
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Warning lights: suspension, terrain response, engine, or transmission</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Strange noises from differentials, transfer case, or under load</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Performance issues: height adjustment faults, overheating, or leaks</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Due for smog or check engine concerns before renewal</span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Services Included
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Scheduled maintenance and fluid services</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Brake, steering, and suspension repairs</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Transfer case, differential, and 4x4-related care</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>
                <Link to="/check-engine-light" className="text-teal-600 hover:underline">
                  Diagnostics for check engine
                </Link>{" "}
                and drivability
              </span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>
                Certified{" "}
                <Link
                  to="/services/smog-emissions-services"
                  className="text-teal-600 hover:underline"
                >
                  smog and emissions testing
                </Link>{" "}
                when required
              </span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Why Choose Us
          </h2>
          <p className="text-sm leading-relaxed text-slate-700 sm:text-base">
            We regularly work with Land Rover and Range Rover vehicles for
            smog and repair. You get a shop that understands import
            expectations and California emissions requirements in one
            place—without the dealer runaround.
          </p>
        </section>

        <BusinessHours tone="light" className="mt-12" />
        <ReviewSection tone="light" className="mt-12" />

        <ServiceAreas tone="light" className="mt-12 border-t border-slate-200 pt-12" />

        <section className="mt-12 rounded-2xl border border-slate-200 bg-teal-50/80 p-6 text-center shadow-sm ring-1 ring-teal-600/10 sm:p-10">
          <h2 className="text-lg font-semibold text-navy-900 sm:text-xl">
            Book Land Rover or Range Rover service
          </h2>
          <p className="mt-2 text-sm text-slate-700 sm:text-base">
            Call {PHONE_DISPLAY} or tap directions to visit our shop on Rio Nedo.
          </p>
          <div className="mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <CallButton className="px-8 py-3 text-base" />
            <DirectionsButton className="px-8 py-3 text-base" />
          </div>
        </section>
      </ServicePageShell>
    </>
  );
};

export default LandRoverRepair;
